/**
 * API layer for Asertis Products & Categories.
 * All requests use authFetch for automatic Bearer token management.
 */
import { authFetch } from "./authService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_SESSION_URL = process.env.REACT_APP_API_SESSION_URL;
const API_CART_URL = process.env.REACT_APP_API_CART_URL;

const normalizeCategory = (category) => ({
  ...category,
  category_id:
    category.category_id ??
    category.app_category_id ??
    category.id ??
    category.value ??
    null,
  name: category.name ?? category.category_name ?? category.label ?? "Sin nombre",
});

const mapProductsResponse = (data) => ({
  products: data.results || [],
  total: data.total || 0,
  totalPages: data.total_pages || 1,
  hasNext: data.has_next || false,
  hasPrev: data.has_prev || false,
  page: data.page || 1,
});

const normalizeSessionPayload = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }

  return {
    ...data,
    sede: data.sede ?? data.cart?.sede ?? data.result?.sede ?? null,
    client: data.client ?? data.cart?.client ?? data.result?.client ?? null,
    min_delivery:
      data.min_delivery ??
      data.cart?.min_delivery ??
      data.result?.min_delivery ??
      data.sede?.min_delivery ??
      data.cart?.sede?.min_delivery ??
      data.result?.sede?.min_delivery ??
      null,
  };
};

/**
 * Get session/temporal order data to extract sede_id, client info, etc.
 * @param {string} sessionId - Session ID from URL params
 */
const getSessionData = async (sessionId) => {
  try {
    const response = await authFetch(
      `${API_SESSION_URL}?session_id=${encodeURIComponent(sessionId)}`
    );
    if (!response.ok) {
      throw new Error(`Error fetching session: ${response.status}`);
    }
    const data = await response.json();
    return normalizeSessionPayload(data);
  } catch (error) {
    console.error("Error al obtener datos de sesion:", error);
    return null;
  }
};

/**
 * Get cart/session data using the token embedded in the shared menu URL.
 * This payload includes sede/client data used to load the catalog.
 * @param {string} token - Shared cart token from URL
 */
const getCartSessionData = async (token) => {
  try {
    const response = await authFetch(
      `${API_CART_URL}${encodeURIComponent(token)}`
    );
    if (!response.ok) {
      throw new Error(`Error fetching cart session: ${response.status}`);
    }
    const data = await response.json();
    return normalizeSessionPayload(data);
  } catch (error) {
    console.error("Error al obtener datos del carrito/sesion:", error);
    return null;
  }
};

/**
 * Get real-time product availability by barcode for a sede.
 * This endpoint is queried only when adding a product to cart.
 *
 * @param {string} barcode - Product barcode from item_barcode
 * @param {string} sedeId - Sede identifier
 * @returns {Object|null} Product detail or null when unavailable/error
 */
const getProductAvailability = async (barcode, sedeId) => {
  if (!barcode || String(barcode).trim() === "" || String(barcode) === "0") {
    return null;
  }

  try {
    const response = await authFetch(`${API_BASE_URL}/products/product/${barcode}`, {
      method: "POST",
      body: JSON.stringify({
        sede_id: String(sedeId),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `Error ${response.status} al validar existencias del producto ${barcode}:`,
        errorText
      );
      throw new Error(`Error checking product availability: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error al validar existencias en tiempo real:", error);
    return null;
  }
};

/**
 * Get categories for a given sede.
 * @param {string} sedeId - Sede identifier
 * @returns {Array} Array of { category_id, name }
 */
const getCategories = async (sedeId) => {
  try {
    const response = await authFetch(
      `${API_BASE_URL}/products/categories?sede_id=${encodeURIComponent(sedeId)}`
    );
    if (!response.ok) {
      throw new Error(`Error fetching categories: ${response.status}`);
    }
    const data = await response.json();
    console.log("Categorias cargadas:", data.total || 0);
    return (data.results || [])
      .map(normalizeCategory)
      .filter((category) => category.category_id);
  } catch (error) {
    console.error("Error al obtener categorias:", error);
    return [];
  }
};

/**
 * Get paginated products filtered by sede and optionally by category.
 * This endpoint must be called with POST + JSON body.
 *
 * @param {string} sedeId - Sede identifier
 * @param {number} page - Page number (1-indexed)
 * @param {number} pageSize - Products per page
 * @param {string|null} categoryId - Optional category filter
 * @returns {{ products, total, totalPages, hasNext, hasPrev, page }}
 */
const getProducts = async (sedeId, page = 1, pageSize = 50, categoryId = null) => {
  const normalizedCategoryId =
    categoryId && typeof categoryId === "object"
      ? categoryId.category_id ?? categoryId.app_category_id ?? null
      : categoryId;

  try {
    const body = {
      sede_id: String(sedeId),
      page,
      page_size: pageSize,
    };

    if (normalizedCategoryId) {
      body.category_id = String(normalizedCategoryId);
    }

    console.log("Solicitando productos por POST:", body);

    const response = await authFetch(`${API_BASE_URL}/products`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error ${response.status} al obtener productos:`, errorText);
      throw new Error(`Error fetching products: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Productos cargados: ${data.results?.length || 0} de ${data.total || 0}`);
    return mapProductsResponse(data);
  } catch (error) {
    console.error("Error al obtener productos:", error);
    return {
      products: [],
      total: 0,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
      page: 1,
    };
  }
};

/**
 * Search products using semantic search API.
 * @param {string} query - Natural language search query
 * @param {string} sedeId - Sede identifier
 * @param {number} limit - Max results to return
 * @returns {Array} Array of matching products
 */
const searchProducts = async (query, sedeId, limit = 20) => {
  try {
    const body = {
      query,
      sede_id: sedeId,
    };

    console.log("Buscando productos:", query);

    const response = await authFetch(
      `${API_BASE_URL}/products/search?limit=${limit}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error ${response.status} buscando productos:`, errorText);
      throw new Error(`Error searching products: ${response.status}`);
    }

    const data = await response.json();
    const results = data.results || data || [];
    console.log(`Busqueda "${query}": ${results.length} resultados`);
    return results;
  } catch (error) {
    console.error("Error en busqueda de productos:", error);
    return [];
  }
};

export {
  getSessionData,
  getCartSessionData,
  getCategories,
  getProducts,
  searchProducts,
  getProductAvailability,
};
